import React, { useEffect, useState } from "react";
import { PulsanteFormazione } from "../components/RichiestaFormazione";
import ProgrammaMieCommesse from "../components/ProgrammaMieCommesse";
import DaGuardareAdesso from "../components/DaGuardareAdesso";
import OreInCorso from "../components/OreInCorso";
import BenvenutoNuovoUtente from "../components/BenvenutoNuovoUtente";
import { normalizzaPiano } from "../utils/pianiNexiora";
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy, getDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import ChecklistOnboarding from "../components/ChecklistOnboarding";
import PrioritaOggi from "../components/PrioritaOggi";
import FatturazioneDashboardWidget from "../components/FatturazioneDashboardWidget";
import SuggerimentiOperativiWidget from "../components/SuggerimentiOperativiWidget";
import FeaturesSettingsPage from "./FeaturesSettingsPage";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { CheckCircle, AlertTriangle, Clock, Bell, Save, Package, Send, FileText, Shield, MessageSquare } from "lucide-react";
import InsightButton from "../components/InsightButton";
import useNotifications, { notificheSupportate, notificheConfigurate } from "../hooks/useNotifications";
import { differenceInDays, parseISO, format } from "date-fns";
import { isTaskOverdue, daysUntilDue } from "../utils/taskHelpers";
import { it } from "date-fns/locale";
import { updateProfile } from "firebase/auth";
import NuovaPassword from "../components/NuovaPassword";
import { erroreNuovaPassword } from "../utils/regolePassword";
import TaskDetailPanel from "../components/TaskDetailPanel";
import { avvisaCambioStato, avvisaCambioStatoSottoattivita } from "../utils/avvisiAttivita";
import { campiCompletamento, avvisoCompletamentoDiAltri } from "../utils/completamentoAttivita";
import SubtaskDetailPanel from "../components/SubtaskDetailPanel";
import { toLocalDateStr } from "../utils/dateUtils";
import { CardMiaFirma } from "../components/MiaFirma";
import PreferenzeDispositivo from "../components/PreferenzeDispositivo";
import { VERSIONE_APP, DATA_VERSIONE } from "../utils/versione";
import { statoPiano, PIANI_INFO, LISTINO } from "../utils/pianiNexiora";

export function HomePage({ onNavigate }) {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId, orgVisitata: organization } = useOrg();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const canSee = useCanSee();

  useEffect(() => {
    if (!currentUser || !effectiveOrgId) return;
    const isAdmin = isOrgAdmin || isPlatformOwner;
    // Niente where combinati (assignedTo + status!=): richiedono un indice composito e,
    // se manca, la query fallisce in silenzio (nessun errore, risultato sempre vuoto).
    // Un solo where + filtro lato client, stesso pattern già usato altrove in Nexiora.
    const q1 = isAdmin
      ? query(collection(db,"tasks"), where("organizationId","==",effectiveOrgId))
      : query(collection(db,"tasks"), where("assignedTo","==",currentUser.uid), where("organizationId","==",effectiveOrgId));
    const q2 = query(collection(db,"projects"), where("organizationId","==",effectiveOrgId));
    const u1 = onSnapshot(q1, snap => setTasks(snap.docs.map(d => ({id:d.id,...d.data()})).filter(t => t.status!=="completata" && !t.archived)));
    const u2 = onSnapshot(q2, snap => {
      const list = snap.docs.map(d => ({id:d.id,...d.data()}))
        // esclude archiviati e cartelle dei clienti (isParent): in "Progetti" vanno solo le commesse
        .filter(p => !p.archived && !p.isParent);
      list.sort((a,b) => (b.createdAt?.toMillis?.()||0) - (a.createdAt?.toMillis?.()||0));
      setProjects(list);
    });
    return () => { u1(); u2(); };
  }, [currentUser, effectiveOrgId, isOrgAdmin, isPlatformOwner]);

  const today = new Date();
  const todayStr = toLocalDateStr(today);
  const scadute = tasks.filter(t => isTaskOverdue(t)).length;
  const inScadenza = tasks.filter(t => t.dueDate && t.dueDate >= todayStr && differenceInDays(parseISO(t.dueDate), today) <= 3).length;

  return (
    <div style={{ padding:32, maxWidth:900, flex:1, overflowY:"auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
        <h1 style={{ fontSize:22, fontWeight:800, letterSpacing:"-0.3px" }}>
          Ciao, {userProfile?.displayName?.split(" ")[0]} 👋
        </h1>
        {organization && (
          <div style={{ display:"flex", alignItems:"center", gap:8, background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:20, padding:"6px 14px" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background: organization.status==="active" ? "#059669" : "#dc2626" }}/>
            <span style={{ fontSize:13, fontWeight:500, color:"#374151" }}>{organization.name}</span>
            {(() => {
              // Piani in vigore: prova gratuita (con i giorni che restano), Pro, Gold — il vecchio "Free" non esiste più
              const fine = organization.trialEndsAt?.toDate ? organization.trialEndsAt.toDate() : null;
              const giorni = fine ? Math.max(0, Math.ceil((fine - new Date()) / 86400000)) : null;
              const piano = normalizzaPiano(organization.plan);
              const etichetta = organization.status === "trialing" ? `Prova gratuita${giorni !== null ? ` · ${giorni} gg` : ""}`
                : piano === "gold" ? "Gold" : piano === "pro" ? "Pro" : null;
              if (!etichetta) return null;
              const info = organization.status === "trialing" ? { bg:"#ede9fe", colore:"#7c3aed" } : PIANI_INFO[piano];
              return <span style={{ fontSize:11, background:info.bg, color:info.colore, padding:"2px 8px", borderRadius:10, fontWeight:600 }}>{etichetta}</span>;
            })()}
          </div>
        )}
      </div>
      <p style={{ color:"#6b7280", fontSize:14, marginBottom:16 }}>{format(today,"EEEE d MMMM yyyy",{locale:it})}</p>

      <BenvenutoNuovoUtente/>

      {/* W425: il programma lavori delle commesse di cui sono responsabile — in cima, sempre visibile */}
      <ProgrammaMieCommesse progetti={projects} onNavigate={onNavigate}/>

      {/* Le mie ore: solo quando il tempo sta girando (W402) */}
      <OreInCorso onNavigate={onNavigate}/>

      {/* Da guardare adesso (ottobre 2026): notifiche, attività importanti, cose che aspettano
          te e moduli non salvati. Ha preso il posto di «Le sezioni pensate per te». */}
      <DaGuardareAdesso onNavigate={onNavigate} progetti={projects}/>


      <PrioritaOggi orgId={effectiveOrgId} onNavigate={onNavigate}/>

      <ChecklistOnboarding onNavigate={onNavigate}/>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10, marginBottom:22 }}>
        {[
          {label:(isOrgAdmin||isPlatformOwner)?"Attività aperte (org.)":"Attività assegnate", value:tasks.length, color:"#7c3aed", clickable:true},
          {label:"In ritardo", value:scadute, color:"#dc2626", clickable:true},
          {label:"In scadenza (3gg)", value:inScadenza, color:"#d97706", clickable:true},
        ].map(s => {
          const attiva = s.value > 0;
          return (
            <div key={s.label} onClick={()=>s.clickable && onNavigate({page:"my-tasks"})}
              style={{ background:"var(--bg)", border:"1px solid var(--border)", borderRadius:"var(--radius)",
                borderLeft:`4px solid ${attiva?s.color:"var(--border)"}`, padding:"10px 14px",
                display:"flex", alignItems:"center", justifyContent:"space-between",
                cursor:s.clickable?"pointer":"default", opacity:attiva?1:0.55, transition:"opacity .15s" }}>
              <span style={{ fontSize:12, color:"var(--text-muted)", fontWeight:500 }}>{s.label}</span>
              <span style={{ fontSize:20, fontWeight:700, color:attiva?s.color:"var(--text-muted)" }}>{s.value}</span>
            </div>
          );
        })}
      </div>

      {/* Home = la tua giornata (ottobre 2026): i riquadri per tutta l'azienda (suggerimenti
          sulle commesse e fatturazione di fine mese) diventano una riga con il conteggio e il
          collegamento al Centro Operativo, dove sono per intero. Chi non può aprire il Centro
          Operativo continua a vederli qui completi, così nessuno perde informazioni. */}
      {canSee("suggerimenti-operativi") && (
        <SuggerimentiOperativiWidget currentUser={currentUser} isOrgAdmin={isOrgAdmin} isPlatformOwner={isPlatformOwner}
          effectiveOrgId={effectiveOrgId} onNavigate={onNavigate} compatto={canSee("control-center")}/>
      )}

      <FatturazioneDashboardWidget currentUser={currentUser} isOrgAdmin={isOrgAdmin} isPlatformOwner={isPlatformOwner}
        effectiveOrgId={effectiveOrgId} onNavigate={onNavigate} compatto={canSee("control-center")}/>
      <div style={{ height:12 }}/>

      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <h3 style={{ fontSize:15, fontWeight:700 }}>Progetti</h3>
        </div>
        {projects.length === 0 ? (
          <div style={{ fontSize:13, color:"#9ca3af", padding:"16px", background:"#f9fafb", borderRadius:8, textAlign:"center" }}>
            Nessun progetto ancora.{" "}
            {isOrgAdmin && (
              <button onClick={() => onNavigate({page:"new-project"})}
                style={{ color:"#7c3aed", background:"none", border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif", fontSize:13 }}>
                Crea il primo →
              </button>
            )}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10 }}>
            {projects.slice(0,6).map(p => (
              <button key={p.id} onClick={() => onNavigate({page:"project", projectId:p.id, project:p})}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:"#fff", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", textAlign:"left", fontFamily:"'Inter',sans-serif", width:"100%", transition:"box-shadow 0.15s" }}
                onMouseOver={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                onMouseOut={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ width:32, height:32, borderRadius:8, background:p.color||"#7c3aed", flexShrink:0 }}/>
                <span style={{ fontSize:13, fontWeight:600, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function NotificationsPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [tasks, setTasks] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [scadenzario, setScadenzario] = useState([]);
  const [nuoveForniture, setNuoveForniture] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [noteSpese, setNoteSpese] = useState([]);
  const [codiciCommessa, setCodiciCommessa] = useState({}); // {projectId: codiceCommessa} — per anteporre sempre il codice, mai denormalizzato sulle singole attività
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [filtro, setFiltro] = useState("tutte");
  const [messaggi, setMessaggi] = useState([]);
  const [soloNonLetti, setSoloNonLetti] = useState(false);
  const { addToast } = useToast();

  // ── Messaggi personali (collezione "notifiche"): commenti, condivisioni, assegnazioni,
  // avvisi automatici. Prima venivano scritti ma nessuna pagina li mostrava.
  useEffect(() => {
    if (!currentUser || !orgId) return;
    const q = query(collection(db,"notifiche"), where("userId","==",currentUser.uid), where("orgId","==",orgId));
    return onSnapshot(q, snap => {
      const ms = (v) => v?.toMillis ? v.toMillis() : (v?.seconds ? v.seconds*1000 : 0);
      setMessaggi(snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b) => ms(b.createdAt) - ms(a.createdAt)).slice(0, 200));
    }, err => console.warn("Notifiche non disponibili:", err?.code || err));
  }, [currentUser, orgId]);

  const segnaLetto = (m) => { if (m && m.letta !== true) updateDoc(doc(db,"notifiche",m.id), { letta:true }).catch(()=>{}); };
  const segnaTutteLette = async () => {
    const daLeggere = messaggi.filter(m => m.letta !== true);
    try {
      for (let i = 0; i < daLeggere.length; i += 400) {
        const b = writeBatch(db);
        daLeggere.slice(i, i+400).forEach(m => b.update(doc(db,"notifiche",m.id), { letta:true }));
        await b.commit();
      }
    } catch (err) { addToast(messaggioErrore(err), "error"); }
  };
  // Dove porta un messaggio: all'attività, alla commessa (con la scheda giusta) o alla pagina del registro.
  const apriMessaggio = async (m) => {
    segnaLetto(m);
    if (m.taskId) {
      try {
        const snap = await getDoc(doc(db,"tasks",m.taskId));
        if (snap.exists()) { setSelectedSubtask(null); setSelectedTask({ id:snap.id, ...snap.data() }); return; }
        addToast("L'attività non esiste più", "info");
      } catch (err) { addToast(messaggioErrore(err), "error"); }
      return;
    }
    if (!onNavigate) return;
    const pagine = {
      elaborazione_offerta_condivisa: { page:"elaborazione-offerte" },
      segnalazione_cliente: { page:"segnalazioni-cliente" },
      segnalazione_cliente_stato: { page:"segnalazioni-cliente" },
      nf_presa_in_carico: { page:"centro-acquisti" },
      pratica: { page:"pratiche-aperte" },
      noleggio_fine: { page:"registro-noleggi" },
      richiesta_fatturazione: { page:"da-fatturare" },
      promemoria_nota: { page:"registro-note" },
      // W446: chi chiede di entrare in azienda o ha accettato un invito → Gestione Utenti
      richiesta_accesso: { page:"users" },
      invito_accettato: { page:"users" },
    };
    // promemoria di una nota importante: si apre il Registro delle mie note (W406)
    if (m.tipo === "promemoria_nota") onNavigate(pagine.promemoria_nota);
    // timbrature dimenticate (W419): chi decide va ad Approvazione Cartellini, l'operaio al suo cartellino
    else if (m.tipo === "giustificazione_presenze") { if (m.pagina) onNavigate({ page: m.pagina }); }
    else if (m.tipo === "giustificazione_presenze_esito" || m.tipo === "promemoria_timbratura") onNavigate({ page: "presenze" });
    // segnalazione cliente: si apre la segnalazione, non la commessa (W409)
    else if (m.tipo === "segnalazione_cliente" || m.tipo === "segnalazione_cliente_stato") onNavigate({ page:"segnalazioni-cliente", segId: m.rifId || undefined });
    else if (m.projectId) onNavigate({ page:"project", projectId:m.projectId, initialTab: m.tipo === "sicurezza_cantiere" ? "sicurezza" : m.tipo === "noleggio_rettifica" ? "cabina-regia" : undefined });
    else if (m.tipo === "richiesta_commerciale") onNavigate({ page:"registro-richieste-comm" });
    else if (pagine[m.tipo]) onNavigate(pagine[m.tipo]);
  };

  // ── Attività scadute / in scadenza ───────────────────────────────────────
  useEffect(() => {
    if (!currentUser || !orgId) return;
    const q = canAdmin
      ? query(collection(db,"tasks"), where("organizationId","==",orgId))
      : query(collection(db,"tasks"), where("assignedTo","==",currentUser.uid), where("organizationId","==",orgId));
    return onSnapshot(q, snap => setTasks(snap.docs.map(d=>({id:d.id,...d.data()})).filter(t=>t.status!=="completata")));
  }, [currentUser, canAdmin, orgId]);

  useEffect(() => {
    if (!currentUser || !orgId) return;
    if (canAdmin) {
      return onSnapshot(query(collection(db,"subtasks"), where("organizationId","==",orgId)),
        snap => setSubtasks(snap.docs.map(d=>({id:d.id,...d.data()}))));
    }
    const q1 = query(collection(db,"subtasks"), where("assignedTo","==",currentUser.uid), where("organizationId","==",orgId));
    const q2 = query(collection(db,"subtasks"), where("createdBy","==",currentUser.uid), where("organizationId","==",orgId));
    let a=[], c=[];
    const merge = () => { const m={}; [...a,...c].forEach(s=>{m[s.id]=s;}); setSubtasks(Object.values(m)); };
    const u1 = onSnapshot(q1, snap=>{a=snap.docs.map(d=>({id:d.id,...d.data()}));merge();});
    const u2 = onSnapshot(q2, snap=>{c=snap.docs.map(d=>({id:d.id,...d.data()}));merge();});
    return ()=>{u1();u2();};
  }, [currentUser, canAdmin, orgId]);

  // ── Scadenziario: voci in scadenza entro 30 giorni (solo admin) ───────────
  useEffect(() => {
    if (!canAdmin || !orgId) return;
    return onSnapshot(query(collection(db,"scadenzario"), where("organizationId","==",orgId)),
      snap => setScadenzario(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [canAdmin, orgId]);

  // ── Nuove Forniture in attesa (solo admin) ────────────────────────────────
  useEffect(() => {
    if (!canAdmin || !orgId) return;
    return onSnapshot(
      query(collection(db,"nuoveForniture"), where("organizationId","==",orgId), where("statoInvio","==","Da inviare")),
      snap => setNuoveForniture(snap.docs.map(d=>({id:d.id,...d.data()})).filter(d=>!d.eliminata)));
  }, [canAdmin, orgId]);

  // ── Presenze da verificare (solo admin) ───────────────────────────────────
  // Nota: dal momento in cui "Invia in accettazione" è stato rimosso (le ore contano in tempo
  // reale), lo stato "inviata" non viene più raggiunto — un cartellino è "da verificare" quando
  // il mese è chiuso (mese < mese corrente) e non è ancora stato approvato. Stessa definizione
  // usata in PresenzePage.js (meseChiuso / scadenzaVerificaSuperata).
  useEffect(() => {
    if (!canAdmin || !orgId) return;
    return onSnapshot(
      query(collection(db,"presenze"), where("orgId","==",orgId)),
      snap => {
        const oggi3 = new Date();
        const daVerificare = snap.docs.map(d=>({id:d.id,...d.data()})).filter(p => {
          const meseChiuso3 = p.anno < oggi3.getFullYear() || (p.anno===oggi3.getFullYear() && (p.mese-1) < oggi3.getMonth());
          return meseChiuso3 && p.stato !== "approvata";
        });
        setPresenze(daVerificare);
      });
  }, [canAdmin, orgId]);

  // ── Note spese in attesa (solo admin) ─────────────────────────────────────
  useEffect(() => {
    if (!canAdmin || !orgId) return;
    return onSnapshot(
      query(collection(db,"noteSpese"), where("orgId","==",orgId), where("stato","==","inviata")),
      snap => setNoteSpese(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [canAdmin, orgId]);

  // ── Codici commessa (per anteporli sempre alle attività, come ovunque nell'app) ──
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"projects"), where("organizationId","==",orgId)),
      snap => setCodiciCommessa(Object.fromEntries(snap.docs.map(d=>[d.id, d.data().codiceCommessa||""]))));
  }, [orgId]);

  // ── Costruzione lista notifiche ───────────────────────────────────────────
  const today = new Date();
  const notifs = [];

  // 1. Attività scadute
  const taskIds = new Set(tasks.map(t=>t.id));
  const visibleSubtasks = canAdmin
    ? subtasks.filter(s=>taskIds.has(s.parentTaskId))
    : subtasks.filter(s=>s.status!=="completata"&&s.status!=="annullata");

  [...tasks, ...visibleSubtasks].forEach(t => {
    if (!t.dueDate) return;
    const days = differenceInDays(parseISO(t.dueDate), today);
    if (isTaskOverdue(t)) {
      notifs.push({ categoria:"attivita", tipo:"scaduta", colore:"#dc2626", icona:AlertTriangle,
        titolo: t.title, sottotitolo: `Scaduta ${Math.abs(days)} giorno/i fa`,
        tag: (codiciCommessa[t.projectId]?`[${codiciCommessa[t.projectId]}] `:"")+(t.projectName||""), task: t });
    } else if (t.reminderEnabled && days <= (t.reminderDays||1)) {
      notifs.push({ categoria:"attivita", tipo:"promemoria", colore:"#d97706", icona:Bell,
        titolo: t.title, sottotitolo: days===0?"Scade oggi!":days===1?"Scade domani":`Scade tra ${days} giorni`,
        tag: (codiciCommessa[t.projectId]?`[${codiciCommessa[t.projectId]}] `:"")+(t.projectName||""), task: t });
    } else if (days <= 3) {
      notifs.push({ categoria:"attivita", tipo:"imminente", colore:"#2563eb", icona:Clock,
        titolo: t.title, sottotitolo: days===0?"Scade oggi!":days===1?"Scade domani":`Scade tra ${days} giorni`,
        tag: (codiciCommessa[t.projectId]?`[${codiciCommessa[t.projectId]}] `:"")+(t.projectName||""), task: t });
    }
  });

  // 2. Scadenziario in scadenza entro 30 giorni (solo admin)
  if (canAdmin) {
    scadenzario.forEach(s => {
      if (!s.dataScadenza) return;
      const days = differenceInDays(parseISO(s.dataScadenza), today);
      const dest = { page:"scadenzario" };
      if (days < 0) {
        notifs.push({ categoria:"scadenzario", tipo:"scaduta", colore:"#dc2626", icona:Shield,
          titolo: s.descrizione || s.tipo || "Scadenza", sottotitolo: `Scaduta ${Math.abs(days)} giorno/i fa`,
          tag: s.categoria === "auto" ? `🚗 ${s.targa||""}` : "📋 Sicurezza", dest });
      } else if (days <= 30) {
        notifs.push({ categoria:"scadenzario", tipo:"imminente", colore: days<=7?"#dc2626":"#d97706", icona:Shield,
          titolo: s.descrizione || s.tipo || "Scadenza", sottotitolo: days===0?"Scade oggi!":days===1?"Scade domani":`Scade tra ${days} giorni`,
          tag: s.categoria === "auto" ? `🚗 ${s.targa||""}` : "📋 Sicurezza", dest });
      }
    });

    // 3. NF in attesa di invio
    nuoveForniture.forEach(nf => {
      notifs.push({ categoria:"acquisti", tipo:"nf", colore:"#7c3aed", icona:Package,
        titolo: `NF da inviare: ${nf.descrizione||nf.fornitore||"—"}`,
        sottotitolo: `Commessa: ${nf.commessaNome||"—"}`,
        tag: "🛒 Centro Acquisti", dest:{page:"centro-acquisti"} });
    });

    // 4. Presenze da verificare (mesi chiusi non ancora approvati)
    presenze.forEach(p => {
      const mesi = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
      const scadenza = new Date((p.anno||today.getFullYear()), (p.mese||1)+1, 10); // giorno 10 del mese dopo
      const scaduto = today >= scadenza;
      notifs.push({ categoria:"presenze", tipo: scaduto?"scaduta":"approvazione", colore: scaduto?"#dc2626":"#059669", icona:Clock,
        titolo: scaduto ? `Cartellino chiuso senza verifica` : `Cartellino da verificare`,
        sottotitolo: `${mesi[(p.mese||1)-1]} ${p.anno||""} — UID: ${p.uid?.slice(0,8)||"—"}`,
        tag: "🕐 Presenze", dest:{page:"presenze", dipId:p.uid} });
    });

    // 5. Note spese in attesa
    noteSpese.forEach(ns => {
      const mesi = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
      notifs.push({ categoria:"notespese", tipo:"approvazione", colore:"#0891b2", icona:FileText,
        titolo: `Nota spese da approvare`,
        sottotitolo: `${mesi[(ns.mese||1)-1]} ${ns.anno||""}`,
        tag: "🧾 Note Spese", dest:{page:"approvazione-note-spese"} });
    });
  }

  // 6. Messaggi personali (sempre in cima i non letti, poi i letti più recenti)
  const nonLetti = messaggi.filter(m => m.letta !== true).length;
  const fmtData = (v) => { const d = v?.toDate ? v.toDate() : null; return d ? format(d, "d MMM yyyy, HH:mm", { locale: it }) : ""; };
  const notifMessaggi = messaggi.filter(m => !soloNonLetti || m.letta !== true).map(m => ({
    categoria:"messaggi", tipo: m.letta === true ? "letto" : "messaggio", colore: m.letta === true ? "#9ca3af" : "#7c3aed",
    icona: m.tipo === "sicurezza_cantiere" ? Shield : MessageSquare,
    titolo: m.titolo || "Messaggio", sottotitolo: m.testo || "", tag: fmtData(m.createdAt), messaggio: m,
  }));

  // Ordine: prima per priorità (scaduta > approvazione > imminente), poi per giorni
  const priorita = { scaduta:0, approvazione:1, nf:2, imminente:3, promemoria:4 };
  notifs.sort((a,b) => (priorita[a.tipo]??9) - (priorita[b.tipo]??9));
  // I messaggi non letti vengono prima degli avvisi; quelli già letti restano in fondo.
  notifs.unshift(...notifMessaggi.filter(n => n.tipo === "messaggio"));
  notifs.push(...notifMessaggi.filter(n => n.tipo === "letto"));

  const categorie = [
    { id:"tutte", label:"Tutte" },
    { id:"messaggi", label:"Messaggi" },
    { id:"attivita", label:"Attività" },
    ...(canAdmin ? [
      { id:"scadenzario", label:"Scadenzario" },
      { id:"acquisti", label:"Acquisti" },
      { id:"presenze", label:"Presenze" },
      { id:"notespese", label:"Note Spese" },
    ] : []),
  ];

  const notifsFiltrate = filtro === "tutte" ? notifs : notifs.filter(n => n.categoria === filtro);

  // Qui compaiono solo le attività di cui si è incaricati (o tutte, per titolare/admin):
  // stesse regole del pannello (utils/completamentoAttivita.js).
  const complete = async t => {
    const sotto = !!t.parentTaskId; // nella lista ci sono anche le sottoattività
    try {
      await updateDoc(doc(db, sotto ? "subtasks" : "tasks", t.id), { status:"completata", ...campiCompletamento("completata", currentUser), updatedAt: serverTimestamp() });
      if (sotto) avvisaCambioStatoSottoattivita(t, "completata", currentUser);
      else avvisaCambioStato(t, "completata", currentUser);
      const viola = avvisoCompletamentoDiAltri(t, currentUser?.uid, sotto);
      if (viola) addToast(viola, "viola", 8000);
    } catch (err) { addToast("Non completata: " + (err?.message || err), "error"); }
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      <div style={{ flex:1, overflowY:"auto", padding:"24px 32px", maxWidth:860 }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>Notifiche</h2>
            <p style={{ fontSize:13, color:"#6b7280" }}>{notifsFiltrate.length} avvisi{nonLetti > 0 ? ` · ${nonLetti} messaggi da leggere` : ""}</p>
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <label style={{ fontSize:12, color:"#374151", display:"flex", alignItems:"center", gap:4, cursor:"pointer" }}>
              <input type="checkbox" checked={soloNonLetti} onChange={e=>setSoloNonLetti(e.target.checked)}/> Solo messaggi da leggere
            </label>
            {nonLetti > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={segnaTutteLette}>Segna tutti come letti</button>
            )}
          </div>
        </div>

        {/* Filtri per categoria */}
        <div style={{ display:"flex", gap:6, marginBottom:20, flexWrap:"wrap" }}>
          {categorie.map(c => (
            <button key={c.id} onClick={()=>setFiltro(c.id)}
              style={{ padding:"5px 14px", borderRadius:20, border:`1px solid ${filtro===c.id?"#7c3aed":"#e5e7eb"}`,
                background: filtro===c.id?"#7c3aed":"#fff", color: filtro===c.id?"#fff":"#374151",
                fontSize:12, fontWeight: filtro===c.id?700:400, cursor:"pointer",
                fontFamily:"'Inter',sans-serif" }}>
              {c.label}
              {c.id!=="tutte" && <span style={{ marginLeft:4, opacity:.6 }}>
                ({notifs.filter(n=>n.categoria===c.id).length})
              </span>}
            </button>
          ))}
        </div>

        {/* Lista notifiche */}
        {notifsFiltrate.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={40} color="#d1d5db"/>
            <h3>Tutto in ordine!</h3>
            <p>Nessuna notifica {filtro!=="tutte"?`nella categoria selezionata`:""}</p>
          </div>
        ) : notifsFiltrate.map((n,i) => (
          <div key={n.messaggio ? `m-${n.messaggio.id}` : i}
            onClick={() => { if (n.messaggio) apriMessaggio(n.messaggio); else if (n.task?.parentTaskId) { setSelectedTask(null); setSelectedSubtask(n.task); } else if (n.task) setSelectedTask(n.task); else if (n.dest && onNavigate) onNavigate(n.dest); }}
            style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px",
              background: n.tipo==="messaggio" ? "#faf5ff" : "#fff", border:`1px solid ${n.colore}33`, borderLeft:`4px solid ${n.colore}`,
              borderRadius:8, marginBottom:8, cursor: (n.task||n.dest||n.messaggio)?"pointer":"default" }}>
            <n.icona size={18} color={n.colore} style={{ flexShrink:0 }}/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight: n.tipo==="letto" ? 500 : 600, color: n.tipo==="letto" ? "#6b7280" : "#111827" }}>{n.titolo}</div>
              <div style={{ fontSize:12, color: n.messaggio ? "#374151" : n.colore, fontWeight: n.messaggio ? 400 : 500, marginTop:1 }}>{n.sottotitolo}</div>
              {n.tag && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{n.tag}</div>}
            </div>
            {n.tipo === "messaggio" && (
              <button className="btn btn-secondary btn-sm" title="Segna come letto senza aprirlo"
                onClick={e=>{e.stopPropagation(); segnaLetto(n.messaggio);}} style={{ flexShrink:0 }}>
                Letto
              </button>
            )}
            {n.task && (
              <button className="btn btn-secondary btn-sm"
                onClick={e=>{e.stopPropagation(); complete(n.task);}}
                style={{ color:"#059669", borderColor:"#059669", flexShrink:0 }}>
                ✓ Completa
              </button>
            )}
          </div>
        ))}
      </div>

      {selectedTask && (
        <TaskDetailPanel task={selectedTask}
          onClose={()=>{setSelectedTask(null);setSelectedSubtask(null);}}
          onUpdate={t=>setSelectedTask(t)}
          onSelectSubtask={st=>setSelectedSubtask(st)}
          selectedSubtaskId={selectedSubtask?.id}/>
      )}
      {selectedSubtask && (
        <SubtaskDetailPanel subtask={selectedSubtask}
          onClose={()=>setSelectedSubtask(null)}
          onUpdated={st=>setSelectedSubtask(st)}
          onDeleted={()=>setSelectedSubtask(null)}/>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { currentUser, userProfile, fetchUserProfile, isOrgAdmin, cambiaPassword, resetPassword } = useAuth();
  const { orgVisitata: organization } = useOrg();
  const { addToast } = useToast();
  const [settingsTab, setSettingsTab] = useState("profilo");
  const [form, setForm] = useState({ displayName:userProfile?.displayName||"", phone:userProfile?.phone||"", department:userProfile?.department||"", whatsapp:userProfile?.whatsapp||"", consensoWhatsapp:userProfile?.consensoWhatsapp||false, emailAssegnazioni:userProfile?.emailAssegnazioni!==false, avvisiGiustificazioniPresenze:userProfile?.avvisiGiustificazioniPresenze!==false, emailProgrammaPM:userProfile?.emailProgrammaPM!==false, notifichePopup:userProfile?.notifichePopup!==false });
  const [pwd, setPwd] = useState({ attuale:"", new:"", confirm:"" });
  const { permesso, richiediPermesso } = useNotifications(currentUser);
  const [loading, setLoading] = useState(false);

  const saveProfile = async e => {
    e.preventDefault(); setLoading(true);
    try {
      await updateProfile(auth.currentUser, {displayName:form.displayName});
      await updateDoc(doc(db,"users",currentUser.uid), {displayName:form.displayName, phone:form.phone, department:form.department, whatsapp:form.whatsapp, consensoWhatsapp:form.consensoWhatsapp, emailAssegnazioni:!!form.emailAssegnazioni, notifichePopup:!!form.notifichePopup, avvisiGiustificazioniPresenze:!!form.avvisiGiustificazioniPresenze, emailProgrammaPM:!!form.emailProgrammaPM});
      await fetchUserProfile(currentUser.uid);
      addToast("Profilo salvato!", "success");
    } catch(err) { addToast(messaggioErrore(err, "salvare il profilo"), "error"); }
    setLoading(false);
  };

  // W447: prima la password attuale (riautenticazione), poi la nuova con le regole di utils/regolePassword.js
  const savePwd = async e => {
    e.preventDefault();
    if (!pwd.attuale) { addToast("Scrivi la password attuale", "error"); return; }
    const err = erroreNuovaPassword(pwd.new, currentUser?.email) || (pwd.new !== pwd.confirm ? "Le due password non coincidono" : null)
      || (pwd.new === pwd.attuale ? "La nuova password è uguale a quella attuale" : null);
    if (err) { addToast(err, "error"); return; }
    setLoading(true);
    try {
      await cambiaPassword(pwd.attuale, pwd.new);
      addToast("Password aggiornata. Sugli altri dispositivi ti verrà chiesto di accedere di nuovo.", "success", 8000);
      setPwd({ attuale:"", new:"", confirm:"" });
    } catch(er) {
      const c = er?.code || "";
      addToast(c === "auth/wrong-password" || c === "auth/invalid-credential" ? "La password attuale non è corretta"
        : c === "auth/too-many-requests" ? "Troppi tentativi: riprova tra qualche minuto, oppure fatti mandare il link per reimpostarla"
        : messaggioErrore(er, "aggiornare la password"), "error");
    }
    setLoading(false);
  };
  const mandaLinkPassword = async () => {
    try { await resetPassword(currentUser?.email); addToast(`Ti abbiamo mandato un'email a ${currentUser?.email} con il link per scegliere una nuova password.`, "success", 9000); }
    catch (er) { addToast(er?.message || "Invio non riuscito: riprova tra qualche minuto.", "error"); }
  };

  return (
    <div style={{ padding:24, maxWidth:700, overflowY:"auto" }}>
      <h2 style={{ fontSize:20, fontWeight:700, marginBottom:16 }}>Impostazioni</h2>

      {/* Tab nav */}
      <div style={{display:"flex",gap:4,marginBottom:24,borderBottom:"1px solid #e5e7eb",paddingBottom:0}}>
        {[
          {id:"profilo",label:"👤 Profilo"},
          {id:"organizzazione",label:"🏢 Organizzazione"},
          ...(isOrgAdmin ? [{id:"funzionalita",label:"⚡ Funzionalità"}] : []),
        ].map(t=>(
          <button key={t.id} onClick={()=>setSettingsTab(t.id)}
            style={{padding:"8px 16px",border:"none",borderBottom:`2px solid ${settingsTab===t.id?"#7c3aed":"transparent"}`,background:"transparent",color:settingsTab===t.id?"#7c3aed":"#6b7280",fontSize:13,fontWeight:settingsTab===t.id?600:400,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Funzionalità */}
      {settingsTab==="funzionalita" && <FeaturesSettingsPage/>}

      {/* Tab Organizzazione */}
      {settingsTab==="organizzazione" && organization && (
        <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:12, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:"#374151" }}>La tua organizzazione</div>
          {[
            {label:"Nome", value:organization.name},
            {label:"Piano", value:(()=>{ const st=statoPiano(organization); return st.pianoPagato?`${PIANI_INFO[st.pianoPagato].label} ✓`:st.inProva?`Prova gratuita (${st.giorniProva} gg)`:"Free"; })(), color:(()=>{ const st=statoPiano(organization); return st.pianoPagato?PIANI_INFO[st.pianoPagato].colore:undefined; })()},
            {label:"Utenti attivi", value:(()=>{ const st=statoPiano(organization); return st.pianoPagato?`${organization.seatsUsed||0}${organization.seats?` su ${organization.seats} posti`:""}`:`${organization.seatsUsed||0}${st.inProva ? " (prova in corso)" : " — nessun piano attivo"}`; })()},
          ].map(item => (
            <div key={item.label} style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6 }}>
              <span style={{ color:"#6b7280" }}>{item.label}</span>
              <span style={{ fontWeight:600, color:item.color||"#111827" }}>{item.value}</span>
            </div>
          ))}
          <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <span style={{ fontSize:12.5, color:"#6b7280", flex:"1 1 220px" }}>Formazione su Nexiora, in video call o in sede: su prenotazione, non obbligatoria.</span>
            <PulsanteFormazione/>
          </div>
        </div>
      )}
      {/* Tab Profilo */}
      {settingsTab==="profilo" && <>
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20, marginBottom:16 }}>
        <h3 style={{ fontSize:14, fontWeight:700, marginBottom:16 }}>Profilo</h3>
        <form onSubmit={saveProfile}>
          <div className="form-group"><label className="form-label">Nome e Cognome</label><input className="input" value={form.displayName} onChange={e => setForm(f => ({...f,displayName:e.target.value}))} required/></div>
          <div className="form-group"><label className="form-label">Telefono</label><input className="input" value={form.phone} onChange={e => setForm(f => ({...f,phone:e.target.value}))} placeholder="+39 333 0000000"/></div>
          <div className="form-group">
            <label className="form-label">Numero WhatsApp per i promemoria — facoltativo</label>
            <input className="input" value={form.whatsapp} onChange={e => setForm(f => ({...f,whatsapp:e.target.value}))} placeholder="+39 333 0000000"/>
            <label style={{display:"flex",alignItems:"center",gap:6,marginTop:6,fontSize:12,color:"#6b7280",cursor:"pointer"}}>
              <input type="checkbox" checked={form.consensoWhatsapp} onChange={e=>setForm(f=>({...f,consensoWhatsapp:e.target.checked}))}/>
              Accetto di ricevere qui i promemoria di manutenzione, oltre che via email
            </label>
            <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Questo canale è disattivato finché la tua azienda non lo abilita dalle Impostazioni Organizzazione — impostalo comunque fin da ora se vuoi essere pronto.</div>
          </div>
          <div className="form-group">
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#374151",cursor:"pointer"}}>
              <input type="checkbox" checked={form.emailAssegnazioni} onChange={e=>setForm(f=>({...f,emailAssegnazioni:e.target.checked}))}/>
              Ricevi un'email quando ti assegnano un'attività, una richiesta commerciale o una segnalazione
            </label>
            <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>L'avviso arriva comunque nella campanella 🔔. Le email di scadenza si regolano su ogni singola attività.</div>
          </div>
          <div className="form-group">
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#374151",cursor:"pointer"}}>
              <input type="checkbox" checked={form.emailProgrammaPM !== false} onChange={e=>setForm(f=>({...f,emailProgrammaPM:e.target.checked}))}/>
              Programma della settimana via email (la domenica sera)
            </label>
            <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Per chi è responsabile di commesse: chi lavora su ognuna, giorno per giorno, nella settimana che inizia.</div>
          </div>
          <div className="form-group">
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#374151",cursor:"pointer"}}>
              <input type="checkbox" checked={form.avvisiGiustificazioniPresenze !== false} onChange={e=>setForm(f=>({...f,avvisiGiustificazioniPresenze:e.target.checked}))}/>
              Avvisami quando un collaboratore dei miei cantieri giustifica una timbratura mancante
            </label>
            <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Vale per le commesse di cui sei responsabile. Chi approva i cartellini e il titolare ricevono comunque l'avviso, perché devono decidere.</div>
          </div>
          {notificheConfigurate() && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#374151",cursor:"pointer"}}>
                <input type="checkbox" checked={form.notifichePopup} onChange={e=>setForm(f=>({...f,notifichePopup:e.target.checked}))}/>
                Ricevi le notifiche popup sui tuoi dispositivi (assegnazioni, scadenze, promemoria 🔔)
              </label>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6,flexWrap:"wrap"}}>
                {!notificheSupportate() ? <span style={{fontSize:11,color:"#9ca3af"}}>Questo browser non supporta le notifiche. Su iPhone/iPad installa Nexiora sulla schermata Home (Condividi → Aggiungi alla schermata Home).</span>
                  : permesso === "granted" ? <span style={{fontSize:11,color:"#059669",fontWeight:600}}>✓ Attive su questo dispositivo</span>
                  : permesso === "denied" ? <span style={{fontSize:11,color:"#dc2626"}}>Bloccate nel browser: riattivale dalle impostazioni del sito (icona del lucchetto accanto all'indirizzo).</span>
                  : <button type="button" onClick={async()=>{ const r = await richiediPermesso(); addToast(r==="granted" ? "Notifiche attivate su questo dispositivo ✅" : "Notifiche non attivate", r==="granted" ? "success" : "info"); }}
                      style={{padding:"5px 12px",borderRadius:7,border:"1px solid #7c3aed",background:"#f5f3ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer"}}>🔔 Attiva su questo dispositivo</button>}
              </div>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Vanno attivate su ogni dispositivo (computer, telefono). Su iPhone/iPad funzionano solo con Nexiora installata sulla schermata Home.</div>
            </div>
          )}
          <div className="form-group"><label className="form-label">Reparto</label><input className="input" value={form.department} onChange={e => setForm(f => ({...f,department:e.target.value}))} placeholder="Es. Manutenzione"/></div>
          <div className="form-group"><label className="form-label">Email</label><input className="input" value={currentUser?.email} disabled style={{ opacity:0.6 }}/></div>
          <button type="submit" className="btn btn-primary" disabled={loading}><Save size={14}/>{loading?"Salvo...":"Salva profilo"}</button>
        </form>
      </div>
      {/* firma salvata per i documenti (components/MiaFirma.js) */}
      <CardMiaFirma/>
      {/* testo più grande e vibrazione, per questo dispositivo (components/PreferenzeDispositivo.js) */}
      <PreferenzeDispositivo/>
      <div style={{ fontSize:11, color:"#9ca3af", margin:"-4px 0 16px 2px" }}>Versione di Nexiora su questo dispositivo: <strong>{VERSIONE_APP}</strong> del {DATA_VERSIONE}</div>
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20 }}>
        <h3 style={{ fontSize:14, fontWeight:700, marginBottom:16 }}>Cambia password</h3>
        <form onSubmit={savePwd}>
          <div className="form-group"><label className="form-label" htmlFor="password-attuale">Password attuale</label><input id="password-attuale" className="input" type="password" autoComplete="current-password" value={pwd.attuale} onChange={e => setPwd(p => ({...p,attuale:e.target.value}))} required/></div>
          <NuovaPassword valore={pwd.new} conferma={pwd.confirm} onValore={v => setPwd(p => ({...p,new:v}))} onConferma={v => setPwd(p => ({...p,confirm:v}))} email={currentUser?.email || ""}/>
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <button type="submit" className="btn btn-secondary" disabled={loading}>{loading?"Salvo...":"Aggiorna password"}</button>
            <button type="button" onClick={mandaLinkPassword} style={{ background:"none", border:"none", color:"#7c3aed", fontSize:12.5, cursor:"pointer", padding:0 }}>Non ricordi quella attuale? Ti mandiamo il link</button>
          </div>
        </form>
      </div>
      </>}
    </div>
  );
}
